// Inventario magazzino centrale — stock corrente con alert sotto-scorta
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, AlertTriangle, Truck } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Table } from '../../components/ui/Table'
import { Badge } from '../../components/ui/Badge'
import { CardSkeleton } from '../../components/ui/Skeleton'
import { useRealtime } from '../../hooks/useRealtime'
import api from '../../lib/api'

interface Material {
  id: string
  code: string
  name: string
  category?: string
  quantity: number
  minStock: number
  unit: string
  location?: string
  supplier?: string
  unitCost?: number
}

interface VanAlert {
  technicianId: string
  alerts: Array<{ materialName: string; currentQuantity: number; minQuantity: number }>
}

export function InventoryPage() {
  const [search, setSearch] = useState('')
  const [onlyLow, setOnlyLow] = useState(false)
  const [vanAlerts, setVanAlerts] = useState<VanAlert[]>([])
  const { subscribe } = useRealtime()

  // Ascolta alert furgoni sotto-scorta in real-time
  useEffect(() => {
    const unsub = subscribe<VanAlert>('warehouse:low_stock_global', (data) => {
      setVanAlerts((prev) => {
        const filtered = prev.filter((a) => a.technicianId !== data.technicianId)
        return [...filtered, data]
      })
    })
    return unsub
  }, [subscribe])

  const { data: materials = [], isLoading } = useQuery({
    queryKey: ['warehouse', 'inventory', 'all'],
    queryFn: async () => {
      const res = await api.get<Material[]>('/warehouse/materials')
      return Array.isArray(res.data) ? res.data : []
    },
    staleTime: 2 * 60 * 1000,
  })

  const filtered = materials.filter((m) => {
    const matchSearch =
      !search ||
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.code.toLowerCase().includes(search.toLowerCase()) ||
      (m.category?.toLowerCase().includes(search.toLowerCase()) ?? false)
    const matchLow = !onlyLow || m.quantity <= m.minStock
    return matchSearch && matchLow
  })

  const lowCount = materials.filter((m) => m.quantity <= m.minStock).length

  const columns = [
    {
      key: 'code',
      header: 'Codice',
      cell: (m: Material) => <span className="font-mono text-xs text-[var(--fc-foreground-muted)]">{m.code}</span>,
      className: 'w-28',
    },
    {
      key: 'name',
      header: 'Materiale',
      cell: (m: Material) => (
        <div>
          <p className="font-medium text-[var(--fc-foreground)]">{m.name}</p>
          {m.category && (
            <p className="text-xs text-[var(--fc-foreground-muted)]">{m.category}</p>
          )}
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Giacenza',
      cell: (m: Material) => {
        const isLow = m.quantity <= m.minStock
        return (
          <span className={`font-bold text-base ${isLow ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-foreground)]'}`}>
            {m.quantity} <span className="text-xs font-normal text-[var(--fc-foreground-muted)]">{m.unit}</span>
          </span>
        )
      },
      className: 'w-28',
    },
    {
      key: 'minStock',
      header: 'Scorta min.',
      cell: (m: Material) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">{m.minStock} {m.unit}</span>
      ),
      className: 'w-28',
    },
    {
      key: 'status',
      header: 'Stato',
      cell: (m: Material) => {
        if (m.quantity === 0) return <Badge variant="danger">Esaurito</Badge>
        if (m.quantity <= m.minStock) return <Badge variant="warning">Sotto scorta</Badge>
        return <Badge variant="success">Disponibile</Badge>
      },
      className: 'w-32',
    },
    {
      key: 'location',
      header: 'Ubicazione',
      cell: (m: Material) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">{m.location ?? '—'}</span>
      ),
      className: 'w-28',
    },
    {
      key: 'unitCost',
      header: 'Costo unit.',
      cell: (m: Material) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">
          {m.unitCost != null ? `€ ${Number(m.unitCost).toFixed(2)}` : '—'}
        </span>
      ),
      className: 'w-28',
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Inventario"
        breadcrumb={[{ label: 'Magazzino', href: '/warehouse' }, { label: 'Inventario' }]}
      />

      {/* Alert furgoni tecnici sotto-scorta (real-time WebSocket) */}
      {vanAlerts.map((va) => (
        <div key={va.technicianId} className="flex items-start gap-2 rounded-lg border border-[var(--fc-danger)] bg-[color-mix(in_srgb,var(--fc-danger)_8%,transparent)] px-4 py-3">
          <Truck className="h-4 w-4 text-[var(--fc-danger)] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[var(--fc-foreground)]">
              Furgone sotto scorta — {va.alerts.length} {va.alerts.length === 1 ? 'materiale' : 'materiali'} da rifornire
            </p>
            <p className="text-xs text-[var(--fc-foreground-muted)] mt-0.5">
              {va.alerts.map((a) => `${a.materialName} (${a.currentQuantity}/${a.minQuantity})`).join(' · ')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setVanAlerts((prev) => prev.filter((a) => a.technicianId !== va.technicianId))}
            className="text-xs text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)] shrink-0"
            aria-label="Chiudi alert"
          >
            ✕
          </button>
        </div>
      ))}

      {/* Alert sotto-scorta magazzino centrale */}
      {lowCount > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-[var(--fc-warning)] bg-[color-mix(in_srgb,var(--fc-warning)_10%,transparent)] px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-[var(--fc-warning)] shrink-0" />
          <p className="text-sm font-medium text-[var(--fc-foreground)]">
            {lowCount} {lowCount === 1 ? 'materiale ha' : 'materiali hanno'} raggiunto o superato la soglia minima di scorta
          </p>
          <button
            type="button"
            onClick={() => setOnlyLow(true)}
            className="ml-auto text-xs font-semibold text-[var(--fc-warning)] hover:underline"
          >
            Mostra solo questi
          </button>
        </div>
      )}

      {/* Barra filtri */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--fc-foreground-muted)]" />
          <input
            type="text"
            placeholder="Cerca per nome, codice, categoria..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] pl-9 pr-3 py-2 text-sm text-[var(--fc-foreground)] placeholder:text-[var(--fc-foreground-muted)] focus:border-[var(--fc-border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-border-focus)]"
          />
        </div>
        <button
          type="button"
          onClick={() => setOnlyLow((v) => !v)}
          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
            onlyLow
              ? 'border-[var(--fc-warning)] bg-[color-mix(in_srgb,var(--fc-warning)_15%,transparent)] text-[var(--fc-warning)]'
              : 'border-[var(--fc-border)] bg-[var(--fc-surface)] text-[var(--fc-foreground-muted)] hover:border-[var(--fc-warning)]'
          }`}
        >
          Solo sotto scorta {onlyLow && lowCount > 0 && `(${lowCount})`}
        </button>
        {(search || onlyLow) && (
          <button
            type="button"
            onClick={() => { setSearch(''); setOnlyLow(false) }}
            className="text-sm text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]"
          >
            Reset filtri
          </button>
        )}
        <span className="ml-auto text-sm text-[var(--fc-foreground-muted)]">
          {filtered.length} di {materials.length} articoli
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => <CardSkeleton key={i} />)}
        </div>
      ) : (
        <Table
          data={filtered}
          columns={columns}
          keyExtractor={(m) => m.id}
          emptyMessage={search || onlyLow ? 'Nessun materiale trovato con i filtri selezionati' : 'Nessun materiale in magazzino'}
        />
      )}
    </div>
  )
}
